import { BehaviorSubject, Observable, of, OperatorFunction, ReplaySubject } from 'rxjs';
import {
  bufferTime,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { concat, first, flatten, groupBy, isEqual, map as lodashMap, maxBy, uniq } from 'lodash-es';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { NimbusSignalRHub } from '@nimbus-global/features/signalr/nimbusSignalR.hub';
import { TeamInfoService } from '@nimbus-library/features/shared/services/team/team-info.service';
import { BaseReportingService } from '@nimbus-global/features/reportingLiveData/_base/base-reporting.service';
import { UserPermissionsService } from '@nimbus-global/features/permissions/services/domain/user-permissions.service';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { UserSessionMapper } from './models/user-session.mapper';
import { IUserSessionHistorical, IUserSessionLive } from './models/user-session.model';
import { ReportingUserSessionsApiClient } from './reporting-user-sessions.api-client';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class ReportingUserSessionsService extends BaseReportingService<IUserSessionLive> {
  private userSessionMapper: UserSessionMapper = new UserSessionMapper();
  private userSessionsLive$: BehaviorSubject<IUserSessionLive[]> = new BehaviorSubject<IUserSessionLive[]>([]);
  private readonly syncStateInitializedSubject$ = new ReplaySubject<void>(1);

  public readonly initialized$: Observable<void>;

  public constructor(
    protected readonly authenticationService: IAuthenticationService,
    nimbusSignalRHub: NimbusSignalRHub,
    protected readonly reportingUserSessionsApiClient: ReportingUserSessionsApiClient,
    protected readonly teamInfoService: TeamInfoService,
    protected readonly userPermissionService: UserPermissionsService,
  ) {
    super(nimbusSignalRHub);
    this.initialized$ = this.syncStateInitializedSubject$.pipe(take(1), shareReplay(1));
  }

  public initialize(): void {
    this.authenticationService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        filter((isAuthenticated) => isAuthenticated),
        switchMap(() =>
          this.nimbusSignalRHub.onUserSessionsUpdated$.pipe(
            startWith([]),
            bufferTime(this.signalRBufferMs),
            map((items) => flatten(items)),
            this.mapTeamInformation(),
            tap((items) => this.nextUserSessionsLive(items)),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe();

    super.initialize();
  }

  public getUserSessionsLive$(): Observable<IUserSessionLive[]> {
    return this.userSessionsLive$.pipe(distinctUntilChanged(isEqual));
  }

  public getUserSessionsLive(): IUserSessionLive[] {
    return this.userSessionsLive$.getValue();
  }

  public getUserSessionsLiveIncoming$(): Observable<IUserSessionLive[]> {
    return this.userSessionsLive$.pipe(
      map((items) => items.filter((x) => this.userSessionMapper.isSelected(x))),
      distinctUntilChanged(isEqual),
    );
  }

  public setUserSessionsLive(sessions: IUserSessionLive[]): void {
    this.userSessionsLive$.next(sessions);
  }

  public getUserSessionsLiveIncomingCount$(): Observable<number> {
    return this.getUserSessionsLiveIncoming$().pipe(
      map((items) => items.length),
      distinctUntilChanged(),
    );
  }

  public getUserSessionsLiveCompleted$(): Observable<IUserSessionLive[]> {
    return this.userSessionsLive$.pipe(map((items) => items.filter((x) => this.userSessionMapper.isCompleted(x))));
  }

  public getUserSessionLiveInAcw(): IUserSessionLive {
    return first(this.getUserSessionsLive().filter((x) => this.userSessionMapper.isInAcw(x)));
  }

  public getUserSessionLiveInAcw$(): Observable<IUserSessionLive | null> {
    return this.userSessionsLive$.pipe(
      map((items) => first(items.filter((x) => this.userSessionMapper.isInAcw(x))) ?? null),
      distinctUntilChanged(isEqual),
    );
  }

  public getUserSessionsHistorical$(take: number, skip: number, search?: string): Observable<IUserSessionHistorical[]> {
    const userInfo = this.authenticationService.getUserInfo();
    if (!userInfo) {
      return;
    }
    return this.reportingUserSessionsApiClient
      .getUserSessionsHistorical(userInfo.userId, userInfo.tenantId, take, skip, search)
      .pipe(this.mapTeamInformation());
  }

  protected syncState(): Observable<IUserSessionLive[]> {
    const userInfo = this.authenticationService.getUserInfo();
    if (!userInfo) {
      this.cleanUpState(); // clear sessions because no user info
      this.syncStateInitializedSubject$.next();
      return of([]);
    }

    return this.userPermissionService.hasPermissions([NimbusPermission.ReadOwnConversations]).pipe(
      switchMap((hasPermission) => {
        if (!hasPermission) {
          this.cleanUpState(); // clear sessions because no permissions to see them
          this.syncStateInitializedSubject$.next();
          return of([]);
        }
        const snapshotBeforeSync = this.userSessionsLive$.getValue();
        return this.addTimeout(
          this.reportingUserSessionsApiClient.getUserSessionsLive(userInfo.userId).pipe(
            this.mapTeamInformation(),
            tap((restItems) => {
              const currentLive = this.userSessionsLive$.getValue();
              const restById = groupBy(restItems, (x) => x.id);
              const snapshotById = groupBy(snapshotBeforeSync, (x) => x.id);
              const signalRDuringWindow = currentLive.filter((item) => {
                const restItem = first(restById[item.id]);
                const wasInSnapshot = !!first(snapshotById[item.id]);
                if (!wasInSnapshot && !restItem) {
                  return true;
                }
                return restItem && item.orderNumber > restItem.orderNumber;
              });
              this.cleanUpState();
              this.nextUserSessionsLive(restItems);
              if (signalRDuringWindow.length) {
                this.nextUserSessionsLive(signalRDuringWindow);
              }
              this.syncStateInitializedSubject$.next();
            }),
          ),
        );
      }),
    );
  }

  protected cleanUpState(): void {
    this.userSessionsLive$.next([]);
  }

  private nextUserSessionsLive(items: IUserSessionLive[]): void {
    this.userSessionsLive$.next(this.mergeUserSessionsLive(items));
  }

  private mergeUserSessionsLive(items: IUserSessionLive[]): IUserSessionLive[] {
    const itemsCurrent: IUserSessionLive[] = this.userSessionsLive$.getValue() || [];
    const itemsCurrentById = groupBy(itemsCurrent, (x) => x.id);
    const itemsWithClientState = items.map((item) => {
      const previousItem = first(itemsCurrentById[item.id]);
      return this.userSessionMapper.preserveClientState(item, previousItem);
    });
    const itemsMerged: IUserSessionLive[] = concat(itemsCurrent, itemsWithClientState);
    const itemsById = groupBy(itemsMerged, (x) => x.id);
    const itemsResult = lodashMap(itemsById, (x) => maxBy(x, (d) => d.orderNumber));
    const itemsResultIdsCompleted = itemsResult
      .filter((x) => this.userSessionMapper.isCompleted(x))
      .map((x) => ({ id: x.id, orderNumber: x.orderNumber }));
    this.appendIdsCompleted(itemsResultIdsCompleted);
    return itemsResult.filter((x) => {
      const isCompleted = this.userSessionMapper.isCompleted(x);
      const completedBeforeOrderNumber = this.getCompletedOrderNumber(x.id);
      return isCompleted || (!isCompleted && (!Number.isFinite(completedBeforeOrderNumber) || x.orderNumber > completedBeforeOrderNumber));
    });
  }

  private mapTeamInformation<T extends IUserSessionLive | IUserSessionHistorical>(): OperatorFunction<T[], T[]> {
    return mergeMap((items) => {
      items = items || [];
      const teamIds = items.filter((x) => !!x.teamId).map((x) => x.teamId);
      return this.teamInfoService.getTeamInformation(uniq(teamIds)).pipe(
        catchError(() => of({})),
        map((data) => {
          items.forEach((item) => {
            const teamInformation = data[item.teamId];
            item.teamInformation = {
              id: teamInformation?.id,
              teamId: teamInformation?.teamId,
              name: teamInformation?.name,
              description: teamInformation?.description,
              serviceName: teamInformation?.serviceName,
              pendingServiceName: teamInformation?.pendingServiceName,
              serviceUpn: teamInformation?.serviceUpn,
              pstnNumber: teamInformation?.pstnNumber,
            };
          });
          return items;
        }),
      );
    });
  }
}
